import { useRef, useCallback } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useGroupStore } from '../stores/groupStore';
import { AppNode, Connection, NodeStatus } from '../types';
import { PROTECTED_FIELDS, GENERATION_STATUS_FIELDS, pick } from '../constants/protectedFields';
import { deepClone } from '../utils/clone';

// 撤销步数上限（文档 3.1.0 推荐 20 步）
const MAX_HISTORY = 20;

interface HistoryStep {
  nodes: AppNode[];
  connections: Connection[];
}

/**
 * 撤销历史 hook（重构版）
 *
 * 核心原则：saveHistory 只由「用户主动操作」触发，系统副作用直接写 store 不记历史。
 * 因此 undo 后无需时间阻塞 —— 没有副作用会调 saveHistory。
 *
 * 快照约定：saveHistory 在变更「之前」调用，捕获操作前状态。
 * history[idx] 即为最近一次操作前的快照，undo 恢复 history[idx] 即回退一步。
 *
 * 去重保护：若当前状态与栈顶快照相同则跳过，防止未预见的副作用污染历史。
 */
export function useUndoHistory() {
  const historyRef = useRef<HistoryStep[]>([]);
  const historyIndexRef = useRef(-1);

  // 轻量快照比对：节点数 + 连接数 + 逐节点关键字段
  function sameSnapshot(a: HistoryStep, b: HistoryStep): boolean {
    if (a.nodes.length !== b.nodes.length) return false;
    if (a.connections.length !== b.connections.length) return false;
    for (let i = 0; i < a.nodes.length; i++) {
      const na = a.nodes[i], nb = b.nodes[i];
      if (na.id !== nb.id) return false;
      if (na.x !== nb.x || na.y !== nb.y) return false;
      if (na.title !== nb.title) return false;
      if (na.width !== nb.width || na.height !== nb.height) return false;
    }
    for (let i = 0; i < a.connections.length; i++) {
      if (a.connections[i].from !== b.connections[i].from) return false;
      if (a.connections[i].to !== b.connections[i].to) return false;
    }
    return true;
  }

  const saveHistory = useCallback(() => {
    try {
      const { nodes, connections } = useNodeStore.getState();
      const currentStep: HistoryStep = {
        nodes: deepClone(nodes),
        connections: deepClone(connections),
      };

      // 去重：与栈顶快照相同则跳过
      const top = historyRef.current[historyIndexRef.current];
      if (top && sameSnapshot(top, currentStep)) return;

      // 截断 idx 之后的快照（undo 后若有残留也会被清除），push 当前快照
      const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
      newHistory.push(currentStep);
      while (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
      }
      historyRef.current = newHistory;
      historyIndexRef.current = newHistory.length - 1;
    } catch (e) {
      console.warn('History save failed:', e);
    }
  }, []);

  const undo = useCallback(() => {
    const idx = historyIndexRef.current;
    // history[idx] 是"最近一次操作前"的快照，idx >= 0 即可撤销一步
    if (idx < 0) return;
    const prev = historyRef.current[idx];
    if (!prev) return;

    // 更新 idx 并截断已消费的快照
    historyIndexRef.current = idx - 1;
    historyRef.current = historyRef.current.slice(0, idx);

    const { nodes: currentNodes, setNodes, setConnections } = useNodeStore.getState();
    const currentNodesMap = new Map(currentNodes.map(n => [n.id, n]));

    // 字段保护策略（文档 3.1.3）：
    // 情况 A：节点在当前 state 已存在 → 保留当前的生成状态 + 生成结果，其余用快照
    // 情况 B：节点在当前 state 不存在（撤销删除）→ 整体用快照恢复，但重置生成状态字段
    const restoredNodes: AppNode[] = prev.nodes.map(snapshotNode => {
      const currentNode = currentNodesMap.get(snapshotNode.id);
      if (currentNode) {
        return {
          ...snapshotNode,
          status: currentNode.status,
          data: {
            ...snapshotNode.data,
            ...pick(currentNode.data as object, PROTECTED_FIELDS),
          },
        } as AppNode;
      }
      const clearedData = { ...snapshotNode.data };
      for (const f of GENERATION_STATUS_FIELDS) {
        (clearedData as Record<string, unknown>)[f] = undefined;
      }
      return {
        ...snapshotNode,
        status: NodeStatus.IDLE,
        data: clearedData,
      } as AppNode;
    });

    setNodes(restoredNodes);
    setConnections(prev.connections);

    // 文档 6.3：撤销后重建分组元数据，保持 groups 与 nodes 一致
    try {
      useGroupStore.getState().rebuildGroupsFromNodesData(restoredNodes);
    } catch (e) {
      console.warn('rebuildGroupsFromNodesData failed on undo:', e);
    }
  }, []);

  return { saveHistory, undo };
}
